import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAudioDevices } from '../../hooks/settings/useAudioDevices';
import { useMicLevelTest } from '../../hooks/settings/useMicLevelTest';
import { useMicrophoneSettingsHandlers } from '../../hooks/settings/useMicrophoneSettingsHandlers';
import { MicrophoneInputSection } from './MicrophoneInputSection';
import { SpeakerOutputSection } from './SpeakerOutputSection';
import layoutStyles from './MicrophoneSettingsLayout.module.css';
import feedbackStyles from './MicrophoneSettingsFeedback.module.css';

interface MicrophoneSettingsProps {
  onClose: () => void;
}

export function MicrophoneSettings({ onClose }: MicrophoneSettingsProps) {
  const { t } = useTranslation();
  const {
    audioInputs,
    audioOutputs,
    selectedInput,
    setSelectedInput,
    selectedOutput,
    setSelectedOutput,
    permissionError,
    isLoading,
    loadDevices,
    saveDevicePreferences,
  } = useAudioDevices();

  const { isTestingMic, micLevel, startMicTest, stopMicTest } = useMicLevelTest(selectedInput);

  const { handleSave, handleMicTestToggle, handleInputChange, playTestSound } =
    useMicrophoneSettingsHandlers({
      isTestingMic,
      startMicTest,
      stopMicTest,
      setSelectedInput,
      saveDevicePreferences,
      onClose,
    });

  return (
    <div className={layoutStyles.container}>
      <div className={layoutStyles.header}>
        <h2 className={layoutStyles.title}>
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
            <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" />
          </svg>
          {t('settings:mic.title')}
        </h2>
        <button className={layoutStyles.closeButton} onClick={onClose}>
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        </button>
      </div>

      <div className={layoutStyles.content}>
        {isLoading ? (
          <div className={feedbackStyles.loading}>
            <div className={feedbackStyles.spinner} />
            <p>{t('settings:mic.loadingDevices')}</p>
          </div>
        ) : permissionError ? (
          <div className={feedbackStyles.error}>
            <svg viewBox="0 0 24 24" width="48" height="48" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            <p>{permissionError}</p>
            <button className={feedbackStyles.retryButton} onClick={loadDevices}>
              {t('settings:mic.tryAgain')}
            </button>
          </div>
        ) : (
          <>
            <MicrophoneInputSection
              audioInputs={audioInputs}
              selectedInput={selectedInput}
              isTestingMic={isTestingMic}
              micLevel={micLevel}
              onInputChange={handleInputChange}
              onMicTestToggle={handleMicTestToggle}
            />
            <SpeakerOutputSection
              audioOutputs={audioOutputs}
              selectedOutput={selectedOutput}
              onOutputChange={setSelectedOutput}
              onTestSpeaker={playTestSound}
            />
          </>
        )}
      </div>

      <div className={layoutStyles.footer}>
        <button className={layoutStyles.cancelButton} onClick={onClose}>
          {t('common:buttons.cancel')}
        </button>
        <button
          className={layoutStyles.saveButton}
          onClick={handleSave}
          disabled={isLoading || !!permissionError}
        >
          {t('settings:mic.saveSettings')}
        </button>
      </div>
    </div>
  );
}

export default MicrophoneSettings;
